import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import Close from '@mui/icons-material/Close';
import DragIndicator from '@mui/icons-material/DragIndicator';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import FilterList from '@mui/icons-material/FilterList';
import VerticalAlignBottom from '@mui/icons-material/VerticalAlignBottom';
import VerticalAlignTop from '@mui/icons-material/VerticalAlignTop';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Draggable, Droppable } from '@hello-pangea/dnd';
import { BuilderPanel } from './StackBuilderLayout';
import { STACK_DROPPABLE_ID } from './reorder';
import EntityLink, { offerPath } from './EntityLink';
import { HOVER_ACTIONS, hoverActionsSx, revealHoverActions } from './hoverActions';
import OfferTypeFilter, { matchesTypes } from './OfferTypeFilter';
import {
  ACCENTS,
  accentColor,
  accentOf,
  displayId,
  entityLinkFor,
  type Accent,
  matchesSearch,
  typeLabel,
  type OfferLike,
} from './offerKinds';

export type StackEntry = {
  key: string;
  offer: OfferLike;
  /** Positions this row takes in the served order; a block takes one per member plus its own. */
  span?: number;
  /** A block's member offers, listed under it when expanded. */
  members?: { id: number; name: string; type?: string }[];
  hasOverride?: boolean;
  /** Extra chips after the type: variations, overrides, duplicates. */
  badges?: React.ReactNode;
};

export interface OfferStackProps {
  title: string;
  entries: StackEntry[];
  /** Filled with the keys currently shown, in order, so drops can be mapped onto the full list. */
  visibleKeysRef: React.MutableRefObject<string[]>;
  selectedKey?: string | null;
  onOpen: (key: string, anchor: HTMLElement) => void;
  onRemove: (key: string) => void;
  onMoveToEdge: (key: string, edge: 'top' | 'bottom') => void;
  /** One visible place up (-1) or down (+1); with a filter on, it steps over the hidden rows. */
  onMoveStep: (key: string, delta: -1 | 1) => void;
  emptyText: string;
  removeLabel: string;
  /** The colours listed in the legend under the list; empty hides it. Defaults to all of them. */
  legend?: Exclude<Accent, null>[];
  /** Shown under the filters, above the rows: a warning about the order as a whole. */
  notice?: React.ReactNode;
}

export const stackDraggableId = (key: string) => `stack-${key}`;

function positionsOf(entries: StackEntry[]) {
  let next = 1;
  return entries.map((entry) => {
    const start = next;
    next += Math.max(1, entry.span ?? 1);
    return [start, next - 1] as const;
  });
}

/**
 * The order being built, top to bottom. Rows drag to reorder and receive drops from the library;
 * each opens its details on click. A filter and an inactive toggle narrow what is shown without
 * changing the order — drops still land beside the rows you can see.
 */
export default function OfferStack({
  title,
  entries,
  visibleKeysRef,
  selectedKey,
  onOpen,
  onRemove,
  onMoveToEdge,
  onMoveStep,
  emptyText,
  removeLabel,
  legend = ACCENTS.map((accent) => accent.value),
  notice,
}: OfferStackProps) {
  const [query, setQuery] = React.useState('');
  const [hideInactive, setHideInactive] = React.useState(false);
  const [types, setTypes] = React.useState<string[]>([]);
  const [expanded, setExpanded] = React.useState<Record<string, boolean>>({});

  const positions = React.useMemo(() => positionsOf(entries), [entries]);
  const inactiveCount = entries.filter((entry) => !entry.offer.isActive).length;
  const shown = entries
    .map((entry, index) => ({ entry, index }))
    .filter(
      ({ entry }) =>
        (!hideInactive || entry.offer.isActive) && matchesTypes(entry.offer, types) && matchesSearch(entry.offer, query)
    );
  visibleKeysRef.current = shown.map(({ entry }) => entry.key);
  const lastPosition = positions[positions.length - 1]?.[1] ?? 0;
  const narrowed = shown.length !== entries.length;

  return (
    <BuilderPanel
      emphasis
      label={title}
      title={title}
      aside={`${narrowed ? `${shown.length} of ` : ''}${entries.length} items, ${lastPosition} positions`}
      toolbar={
        <>
          <Stack direction='row' useFlexGap spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size='small'
              sx={{ flex: '1 1 180px' }}
              placeholder={`Filter this ${title.toLowerCase()}`}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position='start'>
                      <FilterList fontSize='small' />
                    </InputAdornment>
                  ),
                  endAdornment: query ? (
                    <InputAdornment position='end'>
                      <IconButton size='small' aria-label='Clear the filter' onClick={() => setQuery('')}>
                        <Close fontSize='small' />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined,
                },
                htmlInput: { 'aria-label': `Filter this ${title.toLowerCase()}` },
              }}
            />
            <OfferTypeFilter
              offers={entries.map((entry) => entry.offer)}
              value={types}
              onChange={setTypes}
              label={`Offer types in this ${title.toLowerCase()}`}
            />
            <Tooltip title={hideInactive ? 'Show inactive offers' : 'Hide inactive offers'}>
              <span>
                <ToggleButton
                  value='hideInactive'
                  size='small'
                  selected={hideInactive}
                  disabled={inactiveCount === 0 && !hideInactive}
                  onChange={() => setHideInactive((value) => !value)}
                  sx={{ whiteSpace: 'nowrap', gap: 0.5, py: 0.5 }}
                >
                  <VisibilityOff fontSize='small' />
                  {inactiveCount}
                </ToggleButton>
              </span>
            </Tooltip>
          </Stack>
          {notice}
        </>
      }
      footer={
        legend.length ? (
          <Stack
            direction='row'
            useFlexGap
            spacing={1.5}
            sx={{ flexWrap: 'wrap', px: 1.5, py: 0.75, borderTop: 1, borderColor: 'divider', color: 'text.secondary' }}
          >
            {ACCENTS.filter((accent) => legend.includes(accent.value)).map((accent) => (
              <Stack key={accent.value} direction='row' spacing={0.5} sx={{ alignItems: 'center' }}>
                <Box
                  sx={(theme) => ({ width: 4, height: 12, borderRadius: 1, bgcolor: accentColor(accent.value, theme) })}
                />
                <Typography variant='caption'>{accent.label}</Typography>
              </Stack>
            ))}
          </Stack>
        ) : undefined
      }
    >
      <Droppable droppableId={STACK_DROPPABLE_ID}>
        {(provided, snapshot) => (
          <Box
            ref={provided.innerRef}
            {...provided.droppableProps}
            component='ol'
            aria-label={`${title} order`}
            sx={{
              listStyle: 'none',
              m: 0,
              p: 0,
              minHeight: 120,
              borderRadius: 1.5,
              outline: snapshot.isDraggingOver ? 2 : 0,
              outlineColor: 'primary.light',
              outlineStyle: 'dashed',
              outlineOffset: 2,
            }}
          >
            {shown.map(({ entry, index }, visibleIndex) => {
              const [start, end] = positions[index] ?? [index + 1, index + 1];
              const accent = accentOf(entry.offer, entry.hasOverride);
              const { label, id } = displayId(entry.offer);
              const isOpen = Boolean(expanded[entry.key]);
              return (
                <Draggable key={entry.key} draggableId={stackDraggableId(entry.key)} index={visibleIndex}>
                  {(dragProvided, dragSnapshot) => (
                    <Box component='li' ref={dragProvided.innerRef} {...dragProvided.draggableProps} sx={{ mb: 0.75 }}>
                      <Box
                        {...dragProvided.dragHandleProps}
                        aria-label={`Position ${start}: ${entry.offer.name}. Press space to move, enter to open.`}
                        onClick={(event: React.MouseEvent<HTMLElement>) => onOpen(entry.key, event.currentTarget)}
                        onKeyDown={(event: React.KeyboardEvent<HTMLElement>) => {
                          // Space belongs to the drag library (lift, then arrows, then space to drop).
                          if (event.key === 'Enter' && event.target === event.currentTarget)
                            onOpen(entry.key, event.currentTarget);
                        }}
                        sx={(theme) => ({
                          position: 'relative',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          py: 0.875,
                          pl: 0.5,
                          pr: 0.5,
                          bgcolor: entry.offer.isActive ? 'background.default' : 'action.hover',
                          border: 1,
                          borderColor: 'divider',
                          borderLeft: 4,
                          borderLeftColor: accentColor(accent, theme),
                          borderRadius: 1.5,
                          cursor: 'pointer',
                          boxShadow: dragSnapshot.isDragging
                            ? 6
                            : selectedKey === entry.key
                              ? `0 0 0 2px ${theme.palette.primary.main}`
                              : 'none',
                          '&:hover': { borderColor: 'text.disabled', borderLeftColor: accentColor(accent, theme) },
                          ...revealHoverActions,
                        })}
                      >
                        <DragIndicator fontSize='small' sx={{ color: 'text.disabled', flex: '0 0 auto' }} />
                        <EntityLink {...entityLinkFor(entry.offer)} />
                        <Typography
                          variant='body2'
                          sx={{
                            fontWeight: 600,
                            color: 'text.secondary',
                            minWidth: 28,
                            textAlign: 'right',
                            flex: '0 0 auto',
                          }}
                        >
                          {start === end ? start : `${start}–${end}`}
                        </Typography>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            variant='body2'
                            noWrap
                            title={entry.offer.name}
                            sx={{ fontWeight: 500, color: entry.offer.isActive ? 'text.primary' : 'text.secondary' }}
                          >
                            {entry.offer.name}
                          </Typography>
                          <Stack
                            direction='row'
                            useFlexGap
                            spacing={0.75}
                            sx={{ alignItems: 'center', flexWrap: 'wrap', color: 'text.secondary' }}
                          >
                            <Typography variant='caption'>{`${label} ${id}`}</Typography>
                            <Chip
                              label={typeLabel(entry.offer)}
                              size='small'
                              sx={{ height: 18, fontSize: '0.6875rem' }}
                            />
                            {!entry.offer.isActive && (
                              <Chip
                                label='Inactive'
                                size='small'
                                color='error'
                                variant='outlined'
                                sx={{ height: 18, fontSize: '0.6875rem' }}
                              />
                            )}
                            {entry.badges}
                            {entry.members?.length ? (
                              <Button
                                size='small'
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setExpanded((value) => ({ ...value, [entry.key]: !isOpen }));
                                }}
                                endIcon={isOpen ? <ExpandLess fontSize='small' /> : <ExpandMore fontSize='small' />}
                                sx={{ minWidth: 0, py: 0, px: 0.5, fontSize: '0.75rem' }}
                              >
                                {`${entry.members.length} offers`}
                              </Button>
                            ) : null}
                          </Stack>
                        </Box>
                        <Stack className={HOVER_ACTIONS} direction='row' sx={hoverActionsSx}>
                          <Tooltip title='Move up'>
                            <span>
                              <IconButton
                                size='small'
                                aria-label='Move up'
                                disabled={visibleIndex === 0}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  onMoveStep(entry.key, -1);
                                }}
                              >
                                <ArrowUpward fontSize='small' />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title='Move down'>
                            <span>
                              <IconButton
                                size='small'
                                aria-label='Move down'
                                disabled={visibleIndex === shown.length - 1}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  onMoveStep(entry.key, 1);
                                }}
                              >
                                <ArrowDownward fontSize='small' />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title='Move to top'>
                            <IconButton
                              size='small'
                              aria-label='Move to top'
                              onClick={(event) => {
                                event.stopPropagation();
                                onMoveToEdge(entry.key, 'top');
                              }}
                            >
                              <VerticalAlignTop fontSize='small' />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title='Move to bottom'>
                            <IconButton
                              size='small'
                              aria-label='Move to bottom'
                              onClick={(event) => {
                                event.stopPropagation();
                                onMoveToEdge(entry.key, 'bottom');
                              }}
                            >
                              <VerticalAlignBottom fontSize='small' />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={removeLabel}>
                            <IconButton
                              size='small'
                              aria-label={removeLabel}
                              onClick={(event) => {
                                event.stopPropagation();
                                onRemove(entry.key);
                              }}
                            >
                              <Close fontSize='small' />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Box>
                      {isOpen && entry.members?.length ? (
                        <Box
                          component='ol'
                          start={start + 1}
                          sx={{
                            m: 0,
                            mt: 0.5,
                            ml: 5.5,
                            pl: 1.5,
                            borderLeft: 2,
                            borderColor: '#d8b4d8',
                            listStyle: 'none',
                          }}
                        >
                          {entry.members.map((member, memberIndex) => (
                            <Stack
                              key={`${member.id}-${memberIndex}`}
                              component='li'
                              direction='row'
                              spacing={1}
                              sx={{ py: 0.25, color: 'text.secondary', alignItems: 'center' }}
                            >
                              {member.id != null && (
                                <EntityLink to={offerPath(member.id)} label={`Open offer ${member.id} in a new tab`} />
                              )}
                              <Typography variant='caption' sx={{ fontWeight: 600, minWidth: 22, textAlign: 'right' }}>
                                {start + memberIndex + 1}
                              </Typography>
                              <Typography variant='body2' noWrap sx={{ flex: 1, minWidth: 0 }}>
                                {member.name}
                              </Typography>
                            </Stack>
                          ))}
                        </Box>
                      ) : null}
                    </Box>
                  )}
                </Draggable>
              );
            })}
            {provided.placeholder}
            {entries.length === 0 && (
              <Box
                sx={{
                  border: 2,
                  borderStyle: 'dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  py: 4,
                  px: 2,
                  textAlign: 'center',
                }}
              >
                <Typography variant='body2' color='text.secondary'>
                  {emptyText}
                </Typography>
              </Box>
            )}
            {entries.length > 0 && shown.length === 0 && (
              <Typography variant='body2' color='text.secondary' sx={{ textAlign: 'center', py: 3 }}>
                Nothing here matches. Clear the filter to see every row.
              </Typography>
            )}
          </Box>
        )}
      </Droppable>
    </BuilderPanel>
  );
}
